import { makeAutoObservable, runInAction } from 'mobx';
import moment from 'moment';
import 'moment-timezone';
import API, { CheckinSuggestion, getCheckinSuggestions } from '../api/API';
import RootStore from './RootStore';
import { ErrorCode } from './RegistrationStore';
import { StepName } from '../config/Wizard';
import { CHECKIN_SUGGESTION_COOLDOWN_KEY, markShown, wasShownWithin } from '../util/displayCooldown';

export const SUGGESTION_COOLDOWN_MINUTES = 60;

export interface ConfirmSummary {
  successes: CheckinSuggestion[];
  failures: { item: CheckinSuggestion; error: any }[];
}

/** Stable identity for a suggestion (a training period can recur on many dates). */
export const suggestionKey = (s: CheckinSuggestion): string =>
  `${s.trainingPeriodId}-${s.date}-${s.hour}-${s.minute}`;

export class SuggestionStore {
  public suggestions: CheckinSuggestion[] = [];
  public selectedKeys: string[] = [];
  public isLoading = false;
  public shouldDisplay = false;
  public notice: string | null = null;
  // Progress for the sequential confirm (so the button can show "Booking X of Y").
  public confirmTotal = 0;
  public confirmDone = 0;
  // Set when the user reaches the success screen via the dialog itself, so the
  // post-success Trigger 1 doesn't immediately re-open the dialog.
  public cameFromDialog = false;
  private noticeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly rootStore: RootStore) {
    makeAutoObservable(this);
  }

  private get availableCoins(): number {
    return this.rootStore.registrationStore?.user?.latestTrainingPackage?.availableCoins ?? 0;
  }

  /** Coin balance available to spend (for the dialog's running total). */
  get coinsAvailable(): number {
    return this.availableCoins;
  }

  /** Coins left after the current selection. */
  get coinsRemaining(): number {
    return this.availableCoins - this.selectionTotal;
  }

  /** A suggestion is affordable if selecting it keeps the spend within balance. */
  isAffordable(s: CheckinSuggestion): boolean {
    if (this.isSelected(s)) return true;
    return this.selectionTotal + this.coinCost(s) <= this.availableCoins;
  }

  get allSelected(): boolean {
    return this.suggestions.length > 0 && this.selectedKeys.length === this.suggestions.length;
  }

  private get hasUsablePackage(): boolean {
    return !!this.rootStore.registrationStore?.user?.latestTrainingPackage;
  }

  // Coins actually deducted for a suggestion, matching the registration flow.
  private coinCost(s: CheckinSuggestion): number {
    return s.originalCoins - s.discountValue;
  }

  get selectionTotal(): number {
    return this.suggestions
      .filter((s) => this.selectedKeys.includes(suggestionKey(s)))
      .reduce((sum, s) => sum + this.coinCost(s), 0);
  }

  get selectionSavings(): number {
    return this.suggestions
      .filter((s) => this.selectedKeys.includes(suggestionKey(s)))
      .reduce((sum, s) => sum + s.savingCoins, 0);
  }

  get selectedSuggestions(): CheckinSuggestion[] {
    return this.suggestions.filter((s) => this.selectedKeys.includes(suggestionKey(s)));
  }

  isSelected(s: CheckinSuggestion): boolean {
    return this.selectedKeys.includes(suggestionKey(s));
  }

  private showNotice(message: string) {
    this.notice = message;
    if (this.noticeTimer) clearTimeout(this.noticeTimer);
    this.noticeTimer = setTimeout(() => {
      runInAction(() => {
        this.notice = null;
      });
    }, 4000);
  }

  toggle(s: CheckinSuggestion) {
    const key = suggestionKey(s);
    if (this.selectedKeys.includes(key)) {
      this.selectedKeys = this.selectedKeys.filter((k) => k !== key);
      return;
    }
    if (this.selectionTotal + this.coinCost(s) > this.availableCoins) {
      this.showNotice('Not enough coins, please renew your package.');
      return;
    }
    this.selectedKeys = [...this.selectedKeys, key];
  }

  /** Select suggestions in time order until the balance cap is hit. */
  selectAll() {
    let total = 0;
    const keys: string[] = [];
    for (const s of this.suggestions) {
      if (total + this.coinCost(s) > this.availableCoins) {
        continue;
      }
      total += this.coinCost(s);
      keys.push(suggestionKey(s));
    }
    if (keys.length < this.suggestions.length) {
      this.showNotice('Selected as many as your coins allow.');
    }
    this.selectedKeys = keys;
  }

  deselectAll() {
    this.selectedKeys = [];
  }

  /**
   * Sequentially register each selected suggestion through the existing
   * /register endpoint (which re-validates the discount and lead-time window
   * server-side). Decrements the local coin balance per success so the
   * success/partial screens reflect the new total.
   */
  async confirmSelected(): Promise<ConfirmSummary> {
    const selected = this.selectedSuggestions;
    const successes: CheckinSuggestion[] = [];
    const failures: { item: CheckinSuggestion; error: any }[] = [];
    const userId = this.rootStore.registrationStore?.user?.id;

    runInAction(() => {
      this.confirmTotal = selected.length;
      this.confirmDone = 0;
    });

    for (const s of selected) {
      const time = moment
        .tz(s.date, 'Europe/Tallinn')
        .hours(s.hour)
        .minutes(s.minute)
        .seconds(0)
        .milliseconds(0)
        .utc();
      try {
        const result = await API.post('/register', {
          trainingPeriodId: s.trainingPeriodId,
          discountRuleId: s.discountRuleId,
          time,
          userId,
        });
        const parsed = JSON.parse(result);
        const cost = parsed.cost ?? s.originalCoins;
        const discountAmount = parsed.discountAmount ?? s.discountValue;
        runInAction(() => {
          const pkg = this.rootStore.registrationStore?.user?.latestTrainingPackage;
          if (pkg) {
            // Coins deducted = cost - discount_amount (mirrors RegistrationStore.addRegistrations).
            pkg.availableCoins -= (cost - discountAmount);
          }
        });
        successes.push(s);
      } catch (error) {
        failures.push({ item: s, error });
      }
      runInAction(() => {
        this.confirmDone += 1;
      });
    }

    return { successes, failures };
  }

  private suggestionTitle(s: CheckinSuggestion): string {
    const startDate = new Date(`${s.date}T00:00:00`);
    const dateLabel = startDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${dateLabel} ${pad(s.hour)}:${pad(s.minute)} — ${s.trainingTitle}`;
  }

  /** Run confirm, then route to the success / partial-success screen. */
  async confirmAndClose() {
    const { successes, failures } = await this.confirmSelected();
    const registrationStore = this.rootStore.registrationStore;
    const wizardStore = this.rootStore.wizardStore;

    this.dismiss();

    if (successes.length > 0 && this.rootStore.registrationStore?.user?.id) {
      // Refresh streak / leaderboard like the normal registration flow.
      registrationStore.loadStudentProgressSilent();
      registrationStore.loadLeaderboard();
    }

    const toItems = (list: CheckinSuggestion[]) =>
      list.map((s) => ({ trainingPeriodId: s.trainingPeriodId, title: this.suggestionTitle(s) }));

    if (failures.length === 0) {
      runInAction(() => {
        registrationStore.selectedItems = toItems(successes) as any;
        registrationStore.selectedTickets = [];
        registrationStore.selectedRentals = [];
        // Only the full-success path routes through RegistrationSuccessStep,
        // whose countdown calls maybeShowAfterCheckin — so only set the guard
        // here. The partial-success step never consumes it, which would
        // otherwise leak and suppress the next genuine post-checkin dialog.
        this.cameFromDialog = true;
      });
      wizardStore.setStep(StepName.REGISTRATION_SUCCESS);
    } else if (successes.length === 0) {
      const errorMessage = failures[0]?.error?.message;
      runInAction(() => {
        registrationStore.partialSuccesses = [];
        registrationStore.partialFailures = [];
        registrationStore.error = Object.values(ErrorCode).includes(errorMessage as ErrorCode)
          ? { code: errorMessage as ErrorCode }
          : {
              code: ErrorCode.BACKEND_MESSAGE,
              customMessage: errorMessage || 'We could not book the selected classes. Please try again.',
            };
      });
      wizardStore.setStep(StepName.REGISTRATION_FAILED);
    } else {
      runInAction(() => {
        registrationStore.partialSuccesses = toItems(successes) as any;
        registrationStore.partialFailures = failures.map(({ item, error }) => ({
          item: { trainingPeriodId: item.trainingPeriodId, title: this.suggestionTitle(item) },
          error,
        })) as any;
      });
      wizardStore.setStep(StepName.REGISTRATION_PARTIAL_SUCCESS);
    }
  }

  private async fetch(userId: number): Promise<CheckinSuggestion[]> {
    this.isLoading = true;
    try {
      const result = await getCheckinSuggestions(userId);
      runInAction(() => {
        this.suggestions = result;
        this.selectedKeys = [];
      });
      return result;
    } catch {
      runInAction(() => {
        this.suggestions = [];
        this.selectedKeys = [];
      });
      return [];
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * Trigger 1 — right after a successful manual class check-in. Ignores the
   * 60-minute cooldown but still writes the timestamp so an immediate refresh
   * stays quiet.
   */
  async maybeShowAfterCheckin(userId: number) {
    if (this.cameFromDialog) {
      // The success screen we just left was reached by confirming in the
      // dialog; don't bounce it straight back open.
      this.cameFromDialog = false;
      return;
    }
    if (!userId || !this.hasUsablePackage) return;
    const result = await this.fetch(userId);
    if (result.length === 0) return;
    markShown(CHECKIN_SUGGESTION_COOLDOWN_KEY, userId);
    runInAction(() => {
      this.shouldDisplay = true;
    });
  }

  /**
   * Trigger 2 — app open / Classes tab. Honors the 60-minute cooldown.
   */
  async maybeShowOnClassesTab(userId: number) {
    if (!userId || !this.hasUsablePackage) return;
    if (this.shouldDisplay) return; // already pending
    if (wasShownWithin(CHECKIN_SUGGESTION_COOLDOWN_KEY, userId, SUGGESTION_COOLDOWN_MINUTES)) return;
    const result = await this.fetch(userId);
    if (result.length === 0) return;
    markShown(CHECKIN_SUGGESTION_COOLDOWN_KEY, userId);
    runInAction(() => {
      this.shouldDisplay = true;
    });
  }

  dismiss() {
    this.shouldDisplay = false;
    this.selectedKeys = [];
    this.notice = null;
    this.confirmTotal = 0;
    this.confirmDone = 0;
    if (this.noticeTimer) {
      clearTimeout(this.noticeTimer);
      this.noticeTimer = null;
    }
  }
}

export default SuggestionStore;
